import { useMutation } from "@apollo/client";
import { Box, Grid } from "@mui/material";
import { EditableAvatar } from "components/atoms/avatars/EditableAvatar";
import { UserAvatar } from "components/atoms/avatars/UserAvatar";
import { CloudLabel } from "components/atoms/CloudLabel";
import { CloudTextField } from "components/atoms/CloudTextField";
import { FormikTextField } from "components/atoms/formik";
import { CloudDrawer, CloudDrawerProps } from "components/modules/CloudDrawer";
import { PasswordReset } from "components/modules/PasswordReset";
import { ORG_USER_LIST_QUERY } from "components/pages/OrganizationUserListPage";
import { ME_QUERY } from "components/pages/ProfilePage";
import { SUPPORT_USER_LIST_QUERY } from "components/pages/SupportUserListPage";
import { USER_QUERY } from "components/pages/UserPage";
import { useConfig } from "config/configHook";
import { useFirebase } from "firebaseWrapper";
import { useFormik } from "formik";
import { graphql } from "gql";
import { Identity, Privilege } from "gql/graphql";
import _ from "lodash";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isSupportPath } from "utils/privileges/privilegeUtils";
import { usePrivilege } from "utils/privileges/usePrivileges";
import * as Yup from "yup";

export const DELETE_USER = graphql(`
  mutation DeleteUser($userId: String!) {
    deleteIdentity(identityId: $userId)
  }
`);

const UPDATE_ME = graphql(`
  mutation UpdateMe($payload: UpdateIdentityPayload!) {
    updateMe(payload: $payload) {
      id
    }
  }
`);

interface Props {
  open: CloudDrawerProps["open"];
  onClose: CloudDrawerProps["onClose"];
  identity: any;
  userIsMe: boolean;
}

type Values = {
  firstName?: string;
  lastName?: string;
  password?: string;
};

export const EditUserBasicInfo = ({
  open,
  onClose,
  identity,
  userIsMe,
}: Props) => {
  const config = useConfig();
  const firebase = useFirebase(config);
  const location = useLocation();
  const supportPath = isSupportPath(location);
  const navigate = useNavigate();
  const canDeleteUser = usePrivilege(Privilege.IdentityDelete).hasPrivilege;
  const user = identity as Identity;

  const [deleteUser, { client }] = useMutation(DELETE_USER, {
    refetchQueries: [
      { query: SUPPORT_USER_LIST_QUERY },
      {
        query: ORG_USER_LIST_QUERY,
        variables: {
          orgId: _.first(user?.affiliations)?.organization?.id,
        },
      },
    ],
  });

  const [updateMe] = useMutation(UPDATE_ME, {
    refetchQueries: [
      userIsMe
        ? {
            query: ME_QUERY,
          }
        : {
            query: USER_QUERY,
            variables: {
              userId: user?.id,
            },
          },
    ],
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const formik = useFormik<Values>({
    initialValues: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string(),
      lastName: Yup.string(),
    }),
    onSubmit(values) {
      const snackbarId = enqueueSnackbar("Updating your profile...", {
        variant: "loading",
        persist: true,
      });

      if (
        values.firstName !== user.firstName ||
        values.lastName !== user.lastName
      ) {
        updateMe({
          variables: {
            payload: {
              firstName: values.firstName?.trim(),
              lastName: values.lastName?.trim(),
            },
          },
          onCompleted: () => {
            enqueueSnackbar(`Your name was updated`, {
              variant: "success",
            });
            closeSnackbar(snackbarId);
          },
          onError: () => {
            enqueueSnackbar(`Could not update your name`, {
              variant: "error",
            });
            closeSnackbar(snackbarId);
          },
        });
      }

      handleClose();
    },
  });

  useEffect(() => {
    formik.setFieldValue("firstName", user?.firstName ?? "");
    formik.setFieldValue("lastName", user?.lastName ?? "");
  }, []);

  useEffect(() => {
    (() => formik.validateForm())();
  }, []);

  const handleDeleteUser = () => {
    const snackbarId = enqueueSnackbar("Deleting user...", {
      variant: "loading",
      persist: true,
    });

    deleteUser({
      variables: {
        userId: user.id,
      },
      onCompleted: () => {
        enqueueSnackbar(`User was deleted`, {
          variant: "success",
        });
        closeSnackbar(snackbarId);

        if (userIsMe) {
          firebase.logout();
          closeSnackbar();
          navigate("/");
          client.clearStore();
        } else if (supportPath) {
          navigate("/cloud/support/users");
        } else {
          navigate(
            `/cloud/fm/${_.first(user?.affiliations)?.organization?.id}/users`,
          );
        }

        // Force refetch of users now that data has changed
        client.cache.evict({
          fieldName: "identities",
        });
        client.cache.evict({
          fieldName: "identityCount",
        });
        client.cache.gc();
      },
      onError: () => {
        enqueueSnackbar(`Could not delete user`, {
          variant: "error",
        });
        closeSnackbar(snackbarId);
      },
    });
  };

  return (
    <form>
      <CloudDrawer
        open={open}
        onClose={handleClose}
        header={userIsMe ? "Edit My Profile details" : "Edit User details"}
        action={{
          label: "Save changes",
          onClick: formik.handleSubmit,
          disabled:
            !userIsMe ||
            formik.isValid === false ||
            (formik.values.firstName === identity.firstName &&
              formik.values.lastName === identity.lastName),
        }}
        secondaryAction={
          canDeleteUser || userIsMe
            ? {
                label: userIsMe ? "Delete my profile" : "Delete user",
                onClick: handleDeleteUser,
              }
            : undefined
        }
        alertLabel={
          userIsMe
            ? "This will remove your access to the system. Are you sure?"
            : "This will remove this user's access to the system. Are you sure?"
        }
      >
        <EditableAvatar
          defaultAvatar={<UserAvatar size="xlarge" identity={user} />}
        />
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <FormikTextField
              name="firstName"
              formik={formik}
              label="First name"
              placeholder="First name"
              disabled={!userIsMe}
            />
          </Grid>
          <Grid item xs={6}>
            <FormikTextField
              name="lastName"
              formik={formik}
              label="Last name"
              placeholder="Last name"
              disabled={!userIsMe}
            />
          </Grid>
          <Grid item xs={12}>
            <CloudTextField
              disabled
              label="Email address"
              value={user?.email}
            />
          </Grid>
          {userIsMe ? (
            <Grid item xs={12}>
              <Box
                sx={(theme) => ({
                  borderTop: "1px solid " + theme.palette.grey[300],
                })}
              />
              <CloudLabel>Change password</CloudLabel>
              <PasswordReset identity={identity} />
            </Grid>
          ) : null}
        </Grid>
      </CloudDrawer>
    </form>
  );
};
